// store global types
import type { TCreateSlice, TAsyncState } from "@/store";

// dependencies types
import type { TApiZendesk } from "@/apis/zendesk/apiZendesk";

type TStateZendesk = {
    zendeskInitialization: TAsyncState<null, Error>;
};
type TActionsPublicZendesk = {
    initializeZendesk: () => Promise<void>;
};
type TActionsPrivateZendesk = {
    setZendeskInitializationPending: () => void;
    setZendeskInitializationSuccess: () => void;
    setZendeskInitializationError: (error: Error) => void;
};

export type TSliceZendesk = TStateZendesk & TActionsPublicZendesk;

type TSliceDependenciesZendesk = {
    apiZendesk: TApiZendesk;
};

export const initialStateZendesk = {
    zendeskInitialization: {
        status: "idle",
        data: null,
        error: null,
    },
} as const satisfies TStateZendesk;

export const createSliceZendesk: TCreateSlice<
    TSliceZendesk,
    TSliceDependenciesZendesk
> =
    ({ apiZendesk }) =>
    (set, getState) => {
        const actionsPrivate: TActionsPrivateZendesk = {
            setZendeskInitializationPending: () => {
                set(
                    {
                        zendeskInitialization: {
                            status: "pending",
                            data: null,
                            error: null,
                        },
                    },
                    false,
                    "zendesk/setZendeskInitializationPending",
                );
            },
            setZendeskInitializationSuccess: () => {
                set(
                    {
                        zendeskInitialization: {
                            status: "resolved",
                            data: null,
                            error: null,
                        },
                    },
                    false,
                    "zendesk/setZendeskInitializationSuccess",
                );
            },
            setZendeskInitializationError: (error: Error) => {
                set(
                    {
                        zendeskInitialization: {
                            status: "rejected",
                            data: null,
                            error,
                        },
                    },
                    false,
                    "zendesk/setZendeskInitializationError",
                );
            },
        };
        const actionsPublic: TActionsPublicZendesk = {
            initializeZendesk: async () => {
                const currentStatus = getState().zendeskInitialization.status;
                if (currentStatus === "resolved") return;
                if (currentStatus === "pending") return;

                actionsPrivate.setZendeskInitializationPending();

                try {
                    await apiZendesk.initZendesk();
                    actionsPrivate.setZendeskInitializationSuccess();
                } catch (error) {
                    actionsPrivate.setZendeskInitializationError(
                        error as Error,
                    );
                }
            },
        };

        return {
            ...initialStateZendesk,
            ...actionsPublic,
        };
    };
